import React, { memo, useMemo } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, HStack, Stack, Text, VStack } from "@chakra-ui/react"

import { useCore } from "@app/hooks/useCore"
import { useStores } from "@app/hooks/useStores"
import { FormButton } from "@app/components/Form/FormButton"
import { FormInput } from "@app/components/Form/FormInput"
import { Icon } from "@app/components/Icon"
import { Link } from "@app/components/Link"
import { Loader } from "@app/components/Loader/Loader"

type Props = {
  id: string
  sku: string
}

export const ProductStockOverlayForm: React.FC<Props> = memo(({ sku }) => {
  const { product } = useStaticQuery<GatsbyTypes.ProductStockOverlayFormQuery>(graphql`
    query ProductStockOverlayForm {
      product: sanityTemplateProduct {
        additionalStock
        additionalStockEmpty
        additionalStockIn
        additionalStockLow
        additionalStockOut
        additionalStockAvailable
        additionalStockUnavailable
        additionalStockCollect
        additionalStockShow
        additionalStockHide
        additionalStockDirections
        additionalStockPlaceholder
        lowStockThreshold
        noStockThreshold
      }
    }
  `)
  const {
    helpers: { linebreak },
  } = useCore()
  const { errors, handleGeolocate, handleChange, handleSubmit, isLoading, position, search, storesWithStock } = useStores(sku)

  const hasResults = useMemo(() => position && storesWithStock.length > 0 && !isLoading, [isLoading, position, storesWithStock?.length])
  const isEmpty = useMemo(() => position && !(storesWithStock.length > 0) && !isLoading, [isLoading, position, storesWithStock?.length])

  return (
    <VStack
      alignItems="stretch"
      justifyContent="flex-start"
      bg="grey.cloud"
      color="grey.coal"
      flex={1}
      px={[6, 12.5]}
      pt={[8, 12.5]}
      pb={[6, 12.5]}
      spacing={0}
    >
      <Box as="form" onSubmit={handleSubmit} alignItems="stretch" justifyContent="flex-start" flexShrink={0}>
        <FormInput
          autoComplete="postal-code"
          background="grey.cloud"
          color="grey.coal"
          borderRadius="8px"
          errors={errors}
          isDisabled={isLoading}
          onChange={handleChange}
          name="query"
          placeholder={product?.additionalStockPlaceholder}
          value={search}
          rightAddon={
            <HStack alignItems="center" justifyContent="flex-end" spacing={2}>
              <FormButton
                isDisabled={isLoading}
                onClick={handleGeolocate}
                title={product?.additionalStockLocate}
                type="button"
                aria-label={product?.additionalStockLocate}
                variant="ghost"
              >
                <Icon name="geolocation" width={5} />
              </FormButton>
              <FormButton
                isDisabled={isLoading}
                title={product?.additionalStockSubmit}
                type="submit"
                aria-label={product?.additionalStockSubmit}
                variant="ghost"
              >
                <Icon name="search" width={5} />
              </FormButton>
            </HStack>
          }
        />
      </Box>

      {hasResults || isEmpty ? (
        <>
          {hasResults && (
            <Accordion allowToggle pt={[2, 6]}>
              {storesWithStock.map(({ key, address, location, hours, phone, stock, title }) => (
                <AccordionItem key={key} borderColor="grey.fog" _first={{ borderTop: "none" }}>
                  {({ isExpanded }) => (
                    <>
                      <AccordionButton
                        as={VStack}
                        cursor="pointer"
                        alignItems="stretch"
                        justifyContent="flex-start"
                        spacing={3}
                        pr={2}
                        title={isExpanded ? product?.additionalStockHide : product?.additionalStockShow}
                        aria-label={isExpanded ? product?.additionalStockHide : product?.additionalStockShow}
                      >
                        <HStack alignItems="center" justifyContent="space-between">
                          <Box
                            w={3}
                            h={3}
                            color="grey.coal"
                            bg={
                              stock > product?.lowStockThreshold
                                ? "brand.success"
                                : stock > product?.noStockThreshold
                                  ? "brand.sunshine"
                                  : "brand.error"
                            }
                            borderRadius="full"
                          />
                          <Text
                            color="grey.coal"
                            letterSpacing="generous"
                            lineHeight={6}
                            pt={2.5}
                            pb={2}
                            size="sm"
                            pr={3}
                            textTransform="none"
                          >
                            {stock > product?.lowStockThreshold
                              ? product?.additionalStockIn
                              : stock > product?.noStockThreshold
                                ? product?.additionalStockLow
                                : product?.additionalStockOut}
                          </Text>
                          <Text pt={2.5} pb={2} flex={1} size="sm" lineHeight={6} color="grey.coal">
                            {title}
                          </Text>
                          <Box transition="transform 0.2s ease" transform={`rotate(${isExpanded ? 180 : 0}deg)`}>
                            <Icon name={isExpanded ? "minus" : "plus"} width={4} />
                          </Box>
                        </HStack>
                      </AccordionButton>
                      <AccordionPanel pr={5}>
                        <Text size="sm" pb={4} lineHeight={1.71}>
                          {stock > product?.noStockThreshold
                            ? product?.additionalStockCollect + " available"
                            : product?.additionalStockCollect + " unavailable"}
                        </Text>
                        <Stack
                          direction={["column", "row"]}
                          alignItems="flex-start"
                          justifyContent={["flex-start", "space-between"]}
                          flexWrap={["wrap", "nowrap"]}
                          spacing={[4, 4]}
                        >
                          <Box w={[1 / 2, 1 / 3]}>
                            {address?.street && (
                              <Text flex={1} letterSpacing="generous" size="sm" lineHeight={1.71}>
                                {address.street}
                              </Text>
                            )}
                            {phone && (
                              <Text flex={1} letterSpacing="generous" size="sm" lineHeight={1.71}>
                                {phone}
                              </Text>
                            )}
                          </Box>
                          {location?.lat && location?.lng && (
                            <FormButton
                              as={Link}
                              external
                              flexShrink={0}
                              size="underline"
                              title={product?.additionalStockDirections}
                              aria-label={product?.additionalStockDirections}
                              to={`//www.google.com/maps/dir/Current+Location/${location.lat},${location.lng},`}
                              variant="underline"
                            >
                              {product?.additionalStockDirections}
                            </FormButton>
                          )}
                        </Stack>
                        {hours && hours?.length > 0 && (
                          <VStack alignItems="stretch" justifyContent="flex-start" spacing={0} my={4}>
                            {hours?.map(({ key, day, open, close }) => (
                              <HStack key={key} alignItems="center" justifyContent="space-between" maxWidth="350px">
                                <Text letterSpacing="generous" size="sm" w={1 / 2}>
                                  {day}
                                </Text>
                                {open && close && (
                                  <Text letterSpacing="generous" size="sm" w={1 / 2}>
                                    {`${open} - ${close}`}
                                  </Text>
                                )}
                              </HStack>
                            ))}
                          </VStack>
                        )}
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              ))}
            </Accordion>
          )}

          {search && isEmpty && (
            <VStack alignItems="center" justifyContent="center" flex={1} pt={12.5}>
              <Text
                alignSelf="center"
                textAlign="center"
                size="xs"
                variant="caps"
                dangerouslySetInnerHTML={{ __html: linebreak(product?.additionalStockEmpty) }}
              />
            </VStack>
          )}
        </>
      ) : (
        isLoading && (
          <VStack alignItems="center" justifyContent="center" flex={1} pt={12.5}>
            <Loader alignSelf="center" />
          </VStack>
        )
      )}
    </VStack>
  )
})
